import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

/**
 * KanbanColumn — two usage modes:
 *
 * 1. Self-contained (Onboarding, Group Deals):
 *    Pass `headerBg` + `accentColor` → renders its own header with label + count.
 *    Fixed `w-64` width, scrolls internally.
 *
 * 2. Header-less / full-width (Job Board):
 *    Omit `headerBg` → no header rendered.
 *    Set `fullWidth` to fill the parent flex/grid cell instead of w-64.
 */
const KanbanColumn = ({ id, label, cards, accentColor, headerBg, children, fullWidth = false }) => {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div className={`flex flex-col ${fullWidth ? 'flex-1 min-w-0' : 'flex-shrink-0 w-64'} h-full`}>
      {/* Header — only rendered when headerBg is provided */}
      {headerBg && (
        <div
          className="flex items-center justify-between px-3 py-2.5 rounded-t-xl flex-shrink-0"
          style={{ backgroundColor: headerBg }}
        >
          <span className="text-xs font-bold uppercase tracking-wider text-white drop-shadow-sm">
            {label}
          </span>
          <span className="text-xs font-bold bg-white/25 text-white rounded-full px-2 py-0.5 min-w-[22px] text-center">
            {cards?.length ?? 0}
          </span>
        </div>
      )}

      {/* Drop zone */}
      <div
        ref={setNodeRef}
        className={`flex-1 min-h-0 overflow-y-auto p-2 space-y-2 transition-colors duration-150
          ${headerBg ? 'rounded-b-xl' : 'rounded-xl'}
          ${isOver ? 'bg-blue-50/80 ring-2 ring-blue-300 ring-inset' : 'bg-white/60'}`}
        style={{ border: `1px solid ${accentColor}30`, borderTop: headerBg ? 'none' : undefined }}
      >
        <SortableContext items={cards?.map(c => String(c.id)) ?? []} strategy={verticalListSortingStrategy}>
          {children}
        </SortableContext>
        {(!cards || cards.length === 0) && (
          <div className="flex items-center justify-center h-16 text-xs text-gray-400 italic">
            Drop cards here
          </div>
        )}
      </div>
    </div>
  );
};

export default KanbanColumn;
